/**
 * client/src/lib/platform-person-route.ts
 *
 * The ONE declaration of the platform-operator person screen's URL — the
 * wouter pattern and the href builder side by side, so the surface that LINKS
 * to the screen and the surface that REGISTERS it cannot drift apart.
 *
 * Why a shared module rather than a literal in each place: the person link
 * (`EntityLink`'s `person` kind, module 04 Task 3) shipped BEFORE the route
 * (module 06 Task 3, same branch). Until module 06 registers
 * `PLATFORM_PERSON_ROUTE` in `client/src/app/admin-routes.tsx`, the href
 * resolves to NotFound — wouter's existing `/admin/platform/org/:orgId/:tab?`
 * matches a ONE-segment tail and cannot swallow `people/<ref>`. A single
 * exported constant means module 06 registers the same string this builder
 * emits, by construction, and `tests/unit/routing-challenge-routes.test.tsx`
 * carries an `it.todo` that surfaces the obligation in every test run.
 *
 * PRIVACY (Immutable Rule 9): `personRef` is the 64-hex `scopedIdFull` digest
 * carried on the audit wire — NEVER a raw `profiles.id` UUID. The screen is
 * org-scoped by construction: there is no global person page, so a caller with
 * no organisation context must render plain text instead of a link.
 */

/** The wouter route pattern — registered by module 06 Task 3. */
export const PLATFORM_PERSON_ROUTE = "/admin/platform/org/:orgId/people/:personRef";

const enc = encodeURIComponent;

/**
 * Why an operator opened one person's record — the three entry routes the
 * privacy screen promises.
 *
 * It lives HERE, beside the href builder, because it is a URL concern: the
 * screen reads it out of `?reason=` and `EntityLink` writes it in. One
 * declaration means the producer (`platformPersonHref`) and the consumer
 * (`readDisclosureReason`) cannot drift, and neither can the copy table in
 * `platform-person.tsx` that is keyed by this union.
 *
 * It is a UI LABEL, never an authorisation. The server now VALIDATES it
 * (unknown/missing → 400 `bad_reason`) so a read cannot arrive carrying a
 * reason the product does not recognise, but it still STORES nothing and
 * GRANTS nothing: an operator editing the query string to another valid
 * reason changes the words on their own screen and nothing else (#2791 is the
 * disclosure log that would make it a fact).
 *
 * Ruling AN adds `activity_review` — the fourth real entry route. The recent
 * activity feed links straight into a person's record, and before this that
 * arrival silently rendered the VETTING framing ("you are checking …"), which
 * is false about why the operator is there.
 *
 * MIRRORED, NOT IMPORTED. `server/logic/platform/person-in-org.ts` exports the
 * same list under the same name. `client/src/*` MUST NOT import from
 * `server/*` (DEVELOPMENT/code-style.md §Import Ordering & Module Boundaries),
 * so the two copies are pinned equal by
 * `tests/unit/platform-person-disclosure-reason.test.ts`.
 */
export const DISCLOSURE_REASONS = [
  "org_vetting",
  "alert_review",
  "access_decision",
  "activity_review",
] as const;

export type DisclosureReason = (typeof DISCLOSURE_REASONS)[number];

/**
 * Read `?reason=` off a location search string.
 *
 * Total by construction: a missing, empty, unknown or malformed value falls
 * back to `org_vetting` rather than throwing. A label that cannot be trusted
 * must still never be able to break the screen that shows it.
 *
 * @param search the raw location search, with or without the leading `?`.
 */
export function readDisclosureReason(search: string): DisclosureReason {
  const raw = new URLSearchParams(search).get("reason") ?? "";
  return (DISCLOSURE_REASONS as readonly string[]).includes(raw)
    ? (raw as DisclosureReason)
    : "org_vetting";
}

/**
 * Build the href for one person as seen within one organisation.
 *
 * Both params are percent-encoded, exactly as `platformPersonApiPath` below
 * does — the two builders sit side by side and an asymmetry between them is a
 * bug waiting to happen, not a style choice. Legitimate values are a UUID and a
 * 64-hex digest, neither of which encoding changes; the encoding is there so a
 * malformed value cannot break out of its path segment.
 *
 * @param orgId     a real `org_nodes.id` uuid (the ORG arm of
 *                  `ProjectedActingOrg` — a personal node has none).
 * @param personRef the person's scoped digest, never a raw user UUID.
 * @param reason    optional entry-route LABEL, appended as `?reason=`. Omitted
 *                  → today's exact href with no trailing `?`, so no existing
 *                  caller changes. Passing it is what stops a real arrival
 *                  from silently defaulting to the vetting framing: the screen
 *                  falls back to `org_vetting` when the query is absent, which
 *                  is a lie on any other entry route.
 */
export function platformPersonHref(
  orgId: string,
  personRef: string,
  reason?: DisclosureReason,
): string {
  const path = `/admin/platform/org/${enc(orgId)}/people/${enc(personRef)}`;
  return reason ? `${path}?reason=${enc(reason)}` : path;
}

/**
 * Build the API path for the same person — the READ behind the screen above.
 *
 * Lives here, beside `platformPersonHref`, because this module owns BOTH shapes
 * of the person-in-org URL and they are NOT the same string: the screen is
 * `/admin/platform/org/<org>/people/<ref>` and the read is
 * `/api/admin-platform/orgs/<org>/people/<ref>` — different prefix, different
 * segment (`org` vs `orgs`). One module owning both is what keeps a future edit
 * to one from silently orphaning the other; an earlier draft of the fetch
 * helper claimed the API path was the href "with /api in front", which was
 * simply false.
 *
 * Pinned against the Express pattern by
 * `tests/unit/platform-person-payload-minimisation.test.ts`, which substitutes
 * params into `PLATFORM_PERSON_API_ROUTE`
 * (server/routes/admin-platform-directory.routes.ts) and compares.
 *
 * Both params are percent-encoded (house style — cf. `enc()` in
 * `client/src/lib/api/platform-invites.ts`), matching `platformPersonHref`.
 *
 * `reason` is REQUIRED here, unlike on `platformPersonHref` (Ruling AN). The
 * route rejects a missing `?reason=` with 400 `bad_reason`, so an optional
 * parameter would let a caller compile cleanly and 400 at runtime. The screen
 * always has a reason to hand — `readDisclosureReason` is total — so there is
 * no caller that legitimately has none.
 */
export function platformPersonApiPath(
  orgId: string,
  personRef: string,
  reason: DisclosureReason,
): string {
  const path = `/api/admin-platform/orgs/${enc(orgId)}/people/${enc(personRef)}`;
  return `${path}?reason=${enc(reason)}`;
}
